<template>
  <div id="agWfEditor">

    <!-- modals -->

    <ag-startNode-modal
      ref="startNodeModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-startNode-modal>

    <ag-input-modal
      ref="inputModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-input-modal>

    <ag-geoInput-modal
      ref="geoInputModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-geoInput-modal>

    <ag-output-modal
      ref="outputModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode"
      @reloadNodes="reloadNodes">
    </ag-output-modal>

    <ag-switch-modal
      ref="switchModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      :validation="validation"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-switch-modal>

    <ag-splitNode-modal
      ref="splitNodeModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-splitNode-modal>

    <ag-mapper-modal
      ref="mapperModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      :validation="validation"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-mapper-modal>

    <ag-queue-modal
      ref="queueModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-queue-modal>

    <ag-logNode-modal
      ref="logNodeModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode"
    ></ag-logNode-modal>

    <ag-formatter-modal
      ref="formatterModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-formatter-modal>

    <ag-variableNode-modal
      ref="variableNodeModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-variableNode-modal>

    <ag-compareNode-modal
      ref="compareNodeModal"
      :node="modalNode"
      :workflow="workflow"
      :disableUI="disableUI"
      @updatedNode="updatedNode"
      @deletedNode="deletedNode">
    </ag-compareNode-modal>

    <ag-errors-modal
      ref="errorsModal"
      :validation="modalValidation"
      :title="modalValidationTitle"
      @editNode="editNode">
    </ag-errors-modal>

   <b-modal
      ref="deleteNodesModal"
      class="agModal"
      centered
      no-close-on-esc
      no-close-on-backdrop
      @keyup.enter.native="confirmDeleteNodesButton_click">
      <template slot="modal-header">
        <h4 class="agModal-title danger">Delete Nodes?</h4>
      </template>
      Are you sure you want to delete the selected nodes? This cannot be undone.
      <template slot="modal-footer" slot-scope="{cancel}">
        <b-button class="agModal-buttonLeft" @click="cancel">Cancel</b-button>
        <b-button class="agModal-buttonRight" variant="danger" @click="confirmDeleteNodesButton_click">Delete</b-button>
      </template>
    </b-modal>


    <!-- toolbar -->
    <ag-toolbar ref="toolbar">
      <template slot="leftContent">
        <b-dropdown 
          class="agDropdown" 
          no-caret dropright 
          v-b-tooltip.hover
          title="Add node"
          :disabled="disableUI">
          <template slot="button-content">
            <fa icon="plus" />
          </template>
          <b-dropdown-item href="#" @click="addButtonItem_click('compareNode')">
            <fa icon="not-equal" />Compare Node
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('formatter')">
            <fa icon="code" />Formatter
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('geoInput')">
            <fa icon="globe" />GeoInput
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('input')">
            <fa icon="arrow-right" />Input
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('logNode')">
            <fa icon="clipboard-list" />Log Node
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('mapper')">
            <fa icon="map" />Mapper
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('output')">
            <fa icon="arrow-left" />Output
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('queue')">
            <fa icon="clock" />Queue
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('splitNode')">
            <fa icon="code-branch" />Split
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('switch')">
            <fa icon="random" />Switch
          </b-dropdown-item>
          <b-dropdown-item href="#" @click="addButtonItem_click('variableNode')">
            <fa icon="equals" />Variable Node
          </b-dropdown-item>
        </b-dropdown>
        <b-button 
          class="agImageButton" 
          v-b-tooltip.hover
          title="Workflow Metadata"
          @click="metadataButton_click">
          <fa icon="cog" />
        </b-button>
        <b-button 
          class="agImageButton" 
          v-b-tooltip.hover
          title="Logs"
          @click="logButton_click">
          <fa icon="info-circle" />
        </b-button>
        <b-button 
          class="agImageButton" 
          v-b-tooltip.hover
          title="Copy Nodes"
          :disabled="selectedNodes.length == 0"
          @click="copyNodesButton_click">
          <fa icon="copy" />
        </b-button>
        <b-button 
          class="agImageButton" 
          v-b-tooltip.hover
          title="Paste Nodes"
          :disabled="copiedNodes.length == 0"
          @click="pasteNodesButton_click">
          <fa icon="clipboard" />
        </b-button>
        <b-button 
          class="agImageButton" 
          v-b-tooltip.hover
          title="Delete Nodes"
          :disabled="selectedNodes.length == 0"
          @click="deleteNodesButton_click">
          <fa icon="trash"/>
        </b-button>
      </template>
      <template slot="centerContent">
        <div id="agWfEditor-toolbarCenter">
          <b-button
            v-if="!isRunning"
            id="agWfEditor-playButton"
            class="agImageButton"
            v-b-tooltip.hover
            title="Start flow"
            :disabled="disableUI || (this.validation && !this.validation.isValid)"
            @click="startButton_click">
            <fa icon="play" />
          </b-button>
          <b-button
            v-if="isRunning"
            id="agWfEditor-stopButton"
            class="agImageButton"
            v-b-tooltip.hover
            title="Stop flow"
            :disabled="isReadonly"
            @click="stopButton_click">
            <fa icon="stop" />
          </b-button>
        </div>
      </template>
      <template slot="rightContent">
        <b-spinner v-if="isLoading" id="agWfEditor-loading"></b-spinner>
        <div v-else-if="isRunning" id="agWfEditor-inProgress">
            <b-spinner variant="danger" label="Text Centered"></b-spinner>
            <label>Processing</label>
        </div>
        <div v-else-if="disableUI">
          <label id="agWfEditor-readonlyLabel">READ-ONLY MODE</label>
        </div>
      </template>
    </ag-toolbar>

    <!-- workflow metadata drawer -->
    <ag-drawer ref="metadataDrawer" :orientation="'left'" :initialWidth="575" :maxWidth="1250" :minWidth="400">
      <template v-slot:content>
        <b-card>
          <template slot="header">
            <h4 class="agModal-title">Edit Workflow</h4>
          </template>
          <ag-workflow-metadata
            ref="workflowMetadata"
            :workflow="workflow"
            :isLoading="isLoading"
            :isReadonly="isReadonly">
          </ag-workflow-metadata>
          <template slot="footer">
            <b-spinner v-if="isLoading" class="agModal-loadingSpinner"></b-spinner>
            <b-button
              class="agModal-buttonRight"
              variant="primary"
              :disabled="isReadonly || isLoading"
              @click="saveWfButton_click">
              Save
            </b-button>
          </template>
        </b-card>
      </template>
    </ag-drawer>

    <!-- log drawer -->
    <ag-drawer ref="logDrawer" :orientation="'bottom'" :initialHeight="500" :maxHeight="700">
      <template v-slot:navbarCustomButtons>
        <b-button 
          class="agImageButton" 
          v-b-tooltip.hover
          title="Clear logs"
          @click="clearLogsButton_click">
          <fa icon="trash" />
        </b-button>
      </template>
      <template v-slot:content>
        <div id="agWfEditor-logContent">
          <div id="agWfEditor-noLogs" v-if="logs.length == 0">
            <fa icon="clipboard-list" />
            <div>No logs to display</div>
          </div>
          <b-table v-else id="agWfEditor-logTable" :items="logs" :fields="logFields" small striped>
            <template slot="table-colgroup">
              <col class="agWfEditor-logTable-col1" />
              <col class="agWfEditor-logTable-col2" />
              <col class="agWfEditor-logTable-col3" />
            </template>
            <template #cell(time)="data">
              <div>{{getFormattedLogDate(data.value)}}</div>
            </template>
            <template #cell(level)="data">
              <div v-if="data.value==-1">
                <fa class="agWfEditor-logLevel agWfEditor-logLevelSystem" icon="cog" />SYSTEM
              </div>
              <div v-if="data.value==1">
                <fa
                  class="agWfEditor-logLevel agWfEditor-logLevelError"
                  icon="exclamation-triangle"
                />ERROR
              </div>
              <div v-else-if="data.value==2">
                <fa class="agWfEditor-logLevel agWfEditor-logLevelWarn" icon="question-circle" />WARN
              </div>
              <div v-else-if="data.value==3">
                <fa class="agWfEditor-logLevel agWfEditor-logLevelInfo" icon="info-circle" />INFO
              </div>
              <div v-else-if="data.value==4">
                <fa class="agWfEditor-logLevel agWfEditor-logLevelDebug" icon="bug" />DEBUG
              </div>
            </template>
            <template #cell(log)="data">
              <span class="agWfEditor-logMessage">{{data.value}}</span>
            </template>
          </b-table>
        </div>
      </template>
    </ag-drawer>

    <!-- canvas -->
    <b-card id="agWfEditor-card">
      <div
        ref="canvas" 
        id="agWfEditor-canvas" 
        :key="reload"
        @click="canvas_click"
        @mousemove="canvas_mousemove">
        <drag-select 
          id="agWfEditor-dragSelect"
          attribute="dragSelectKey"
          :multiSelectKeys="['Shift']"
          @change="dragSelect_selectionChange">
          <ag-node
            v-for="node in nodes"
            :key="node.object._id"
            :ref="node.object._id"
            :dragSelectKey="node.object._id"
            :isSelected="node.isSelected"
            :isReadonly="disableUI"
            :type="node.type"
            :object="node.object"
            :errors="node.errors"
            :warnings="node.warnings"
            @click.native="node_click(node, $event)"
            @dblclick.native="editNode(node)"
            @showErrors="showErrors(node)">
          </ag-node>
        </drag-select>
      </div>
      <div v-if="validation" id="agWfEditor-errors" 
        v-b-tooltip.hover
        title="Errors/Warnings"
        @click="errorsButton_click">
        <fa
          v-if="validation.errors.length > 0"
          id="agWfEditor-errorsIcon"
          icon="exclamation-circle"/>
        <fa
          v-else-if="validation.warnings.length > 0"
          id="agWfEditor-warningsIcon"
          icon="exclamation-triangle"/>
      </div>
    </b-card>
  </div>
</template>





<script>
// components
import Toolbar from "ui/Toolbar";
import Node from "nodes/Node";
import StartNodeModal from "nodes/StartNodeModal";
import InputModal from "nodes/InputModal";
import GeoInputModal from "nodes/geoInputs/GeoInputModal";
import OutputModal from "nodes/OutputModal";
import SwitchModal from "nodes/SwitchModal";
import SplitNodeModal from "nodes/SplitNodeModal";
import MapperModal from "nodes/MapperModal";
import QueueModal from "nodes/queues/QueueModal";
import LogNodeModal from "nodes/LogNodeModal";
import FormatterModal from "nodes/formatters/FormatterModal";
import VariableNodeModal from "nodes/VariableNodeModal";
import CompareNodeModal from "nodes/CompareNodeModal";
import ErrorsModal from "workflow/ErrorsModal";
import Drawer from "ui/Drawer";
import WorkflowMetadata from "workflow/WorkflowMetadata";

// modules
import Server from "src/server";
import Util from "src/util/util";
import JSPlumbUtil from "src/util/jsPlumbUtil";
import { jsPlumb as JSPlumb, jsPlumbUtil } from "jsplumb";
import { EventBus } from "src/eventBus";
import Moment from "moment";
import { Datetime } from "vue-datetime";
import DragSelect from "drag-select-vue";

export default {
  name: "ag-workflow-editor",
  components: {
    "ag-toolbar": Toolbar,
    "ag-node": Node,
    "ag-startNode-modal": StartNodeModal,
    "ag-input-modal": InputModal,
    "ag-geoInput-modal": GeoInputModal,
    "ag-output-modal": OutputModal,
    "ag-switch-modal": SwitchModal,
    "ag-splitNode-modal": SplitNodeModal,
    "ag-mapper-modal": MapperModal,
    "ag-queue-modal": QueueModal,
    "ag-logNode-modal": LogNodeModal,
    "ag-formatter-modal": FormatterModal,
    "ag-variableNode-modal": VariableNodeModal,
    "ag-compareNode-modal": CompareNodeModal,
    "ag-errors-modal": ErrorsModal,
    "ag-drawer": Drawer,
    "ag-workflow-metadata": WorkflowMetadata,
    "datetime": Datetime,
    "drag-select": DragSelect
  },
  props: {},
  data() {
    return {
      reload: 0,
      isReadonly: false,
      isRunning: false,
      isQueued: false,
      isLoading: false,
      runningId: null,
      nodes: [],
      newNodes: [],
      selectedNodes: [],
      isShifted: false,
      isDragging: false,
      validation: null,
      modalNode: null,
      modalValidation: null,
      modalValidationTitle: null,
      webSocket: null,

      // log drawer
      logs: [],
      logFields: [
        { key: "time", label: "Date/Time", sortable: true },
        { key: "level", label: "Level" },
        { key: "log", label: "Message" }
      ],

    };
  },

  computed: {

    disableUI() {
      if(this.isReadonly || this.isRunning || this.isQueued) 
        return true;
      return false;
    },

    workflow() {
      return this.$store.state.wfStore.workflow;
    },

    nodeTypes() {
      return this.$store.state.nodeStore.nodeTypes.filter((a) => !a.restrictInsert);
    },

    copiedNodes() {
      return this.$store.state.nodeStore.copiedNodes;
    },

    user() {
      return this.$store.state.userStore.user;
    },

    logItems() {
      var logItems = [];
      for (var i = 0; i < this.logs.length; i++) {
        var log = this.logs[i];
        logItems.push({
          "Date/Time": Moment(log.time).format("M/DD/YY h:mm:ss a"),
          level: log.level,
          log: log.log
        });
      }
      return logItems;
    }
  },

  watch: {
    disableUI: function() {
      this.reloadNodes();
    },
  },

  created() {
    this.setNodes();
    this.validateWorkflow();

    EventBus.$on("node_dragStop", e => {
      this.node_dragStop(e);
    });

    this.create_websocket();
  },

  mounted() {
            
    window.addEventListener("beforeunload", () => {
      this.webSocket.close();
      this.$store.dispatch("wfStore/releaseWorkflowLock", {workflow: this.workflow});
    });

    window.addEventListener("keydown", (e) => {
      if(e.key == "Shift")
        this.isShifted = true;
    });

    window.addEventListener("keyup", (e) => {
      if(e.key == "Shift")
        this.isShifted = false;
    });

    // setup js plumb
    JSPlumb.ready(() => {
      this.initJSPlumb();
    });


    Server.get("checkEngine").promise.then(result => {
       if(!result.data.success) {
        EventBus.$emit("showAlert", {
          title: "Engine Down",
          message: result.data.error,
          type: "error"
        });
       }
    });   
  },

  beforeDestroy() {
    JSPlumbUtil.reset();
    this.webSocket.close();
    this.$store.dispatch("wfStore/releaseWorkflowLock", {workflow: this.workflow});
  },

  methods: {
    // JS Plumb

    initJSPlumb() {
      var jsPlumb = JSPlumb.getInstance();
      JSPlumbUtil.setJSPlumb(jsPlumb, this.isReadonly, this.$store);

      // setup endpoints
      JSPlumbUtil.initEndpoints(this.nodes, this.newNodes);

      // setup connections
      JSPlumbUtil.initConnections(this.nodes, this.newNodes);

      // setup dragging
      JSPlumbUtil.initDraggable(document.getElementsByClassName("agNode"));

      /* *** NOTE: bind events after creating connections above so we aren't resaving all the src/dest *** */

      // bind before drop event
      jsPlumb.bind("beforeDrop", params => {
        return this.node_beforeDrop(params);
      });

      // bind connection event
      jsPlumb.bind("connection", params => {
        this.node_connection(params);
      });

      // bind detach event
      jsPlumb.bind("connectionDetached", params => {
        this.node_connectionDetached(params);
      });

      // bind move event
      jsPlumb.bind("connectionMoved", params => {
        this.node_connectionMoved(params);
      });
    },

    async node_beforeDrop(params) {
      try {
        let lockCheck = await Server.post("/isWorkflowLocked", {workflow: this.workflow._id}).promise;
        if(!lockCheck.data.success)
          throw lockCheck.data.error;
        else if(lockCheck.data.locked) {
          this.workflowLocked(lockCheck.data.user);
          return false;
        }

        var srcNode = this.nodes.find(a => a.object._id == params.sourceId);
        var destNode = this.nodes.find(a => a.object._id == params.targetId);
        return JSPlumbUtil.validateConnection(srcNode, destNode);
      }
      catch(e) {
        console.log(`Failed 'isWorkflowLocked' server call. ${e.toString()}`);
          EventBus.$emit("showAlert", {
            title: "Error",
            message: e,
            type: "error"
          });
      }
      
    },

    async node_connection(params) {
      let lockCheck = await this.checkWorkflowLock();
      if(lockCheck)
        return;

      var srcNode = this.nodes.find(a => a.object._id == params.sourceId);
      var destNode = this.nodes.find(a => a.object._id == params.targetId);
      if (!destNode.object.src) 
        destNode.object.src = [];

      if (srcNode && destNode) {
        // dests from cases
        if (srcNode.type == "switch") {
          var caseIndex = params.sourceEndpoint.getParameters().caseIndex;
          var agCase = srcNode.object.cases[caseIndex];
          agCase.dest = {
            _id: destNode.object._id,
            type: destNode.type,
            shortname: destNode.object.shortname
          };
        }
        // dests from paths
        else if (srcNode.type == "splitNode") {
          var pathIndex = params.sourceEndpoint.getParameters().pathIndex;
          var agPath = srcNode.object.paths[pathIndex];
          agPath.dest = {
            _id: destNode.object._id,
            type: destNode.type,
            shortname: destNode.object.shortname
          };
        }
        // named dests
        else if(srcNode.type == "compareNode" || srcNode.type == "input" || srcNode.type == "output") {
          const destName = params.sourceEndpoint.getParameters().destName;
          srcNode.object[destName] = {
              _id: destNode.object._id,
              type: destNode.type,
              shortname: destNode.object.shortname
          };
        }
        // everything else has one dest
        else {
          srcNode.object.dest = {
            _id: destNode.object._id,
            type: destNode.type,
            shortname: destNode.object.shortname
          };
        }

        var src = {
          _id: srcNode.object._id,
          type: srcNode.type,
          shortname: srcNode.object.shortname
        };
        let inDest = destNode.object.src.find(
          a => a.shortname == srcNode.object.shortname
        );
        if (!inDest) 
          destNode.object.src.push(src);

        await this.$store.dispatch("nodeStore/updateNode", { node: srcNode });
        await this.$store.dispatch("nodeStore/updateNode", { node: destNode });
        this.validateWorkflow();
      }
    },

    async node_connectionDetached(params) {

      let lockCheck = await this.checkWorkflowLock();
      if(lockCheck)
        return;

      var srcNode = this.nodes.find(a => a.object._id == params.sourceId);
      var destNode = this.nodes.find(a => a.object._id == params.targetId);

      if (srcNode && destNode) {
        // dests from cases
        if (srcNode.type == "switch") {
          let caseIndex = -1;
          if(params.sourceEndpoint)
            caseIndex = params.sourceEndpoint.getParameters().caseIndex;

          let agCase = srcNode.object.cases[caseIndex];
          if(agCase)
            agCase.dest = null;
        } 
        // dests from paths
        else if (srcNode.type == "splitNode") {
          var pathIndex = params.sourceEndpoint.getParameters().pathIndex;
          var agPath = srcNode.object.paths[pathIndex];
          if(agPath)
            agPath.dest = null;
        }  
        // named dests
        else if(srcNode.type == "compareNode" || srcNode.type == "input" || srcNode.type == "output" ) {
          let destName;
          if(params.sourceEndpoint)
            destName = params.sourceEndpoint.getParameters().destName;
          
          if(destName) {
            srcNode.object[destName]._id = null;
            srcNode.object[destName].shortname = null;
          }
        }
        // all other nodes
        else {
          srcNode.object.dest = null;
        }

        destNode.object.src = Util.removeFromArrayByKey(
          destNode.object.src,
          "shortname",
          srcNode.object.shortname
        );

        await this.$store.dispatch("nodeStore/updateNode", { node: srcNode });
        await this.$store.dispatch("nodeStore/updateNode", { node: destNode });
        this.validateWorkflow();
      }
    },

    async node_connectionMoved(params) {

      // same connection made
      if (params.originalTargetId == params.newTargetId && params.originalSourceId == params.newSourceId)
        return;

      var newParams = {
        sourceId: params.originalSourceId,
        targetId: params.originalTargetId
      };
      this.node_connectionDetached(newParams);
    },

    async node_dragStop(e) {
      let lockCheck = await this.checkWorkflowLock();
      if(lockCheck)
        return;

      var dragNode = this.nodes.find(a => a.object._id == e.el.id);
      if(!dragNode) 
        return;

      let dx = e.pos[0] - dragNode.object.x;
      let dy = e.pos[1] - dragNode.object.y;

      if (dx == 0 && dy == 0){
        return;
      }
      dragNode.object.x += dx;
      dragNode.object.y += dy;
      dragNode.x = dragNode.object.x;
      dragNode.y = dragNode.object.y;
      await this.$store.dispatch("nodeStore/updateNodeXY", { node: dragNode });

      for(let i = 0; i < this.selectedNodes.length; i++) {
        let node = this.selectedNodes[i];
        if(node.object._id == dragNode.object._id) // dragNode may or may not be "selected" (highlighted red), so update dragNode outside of selected loop
          continue;

        node.object.x += dx;
        node.object.y += dy;
        node.x = node.object.x;
        node.y = node.object.y;
        
        await this.$store.dispatch("nodeStore/updateNodeXY", { node: node });
      }
    },

    // UI Events

    async canvas_click(e) {
      if(this.isDragging) {
        this.isDragging = false;
        return;
      }
      JSPlumbUtil.clearDragSelection();

      for(var i = 0; i < this.selectedNodes.length; i++) {
        var selectedNode = this.selectedNodes[i];
        selectedNode.isSelected = false;
      }
      this.selectedNodes = [];

      // place pasted nodes
      if(this.newNodes.length > 0) {
        for(var i = 0; i < this.newNodes.length; i++) {
          var newNode = this.newNodes[i];
          var node = this.nodes.find((node) => node.object._id == newNode._id);
          node.object.x = newNode.x;
          node.object.y = newNode.y;
          var result = await this.$store.dispatch("nodeStore/updateNode", { node: node });
          if(result.data.success) {
            let updatedNode = result.data.node;

            // replace node in workflow w/ updated node
            var plural = this.$store.getters["nodeStore/getPlural"](node.type);
            Util.replaceInArray(this.workflow[plural], "_id", updatedNode);
          }
        }      
        this.newNodes = [];
        this.reloadNodes();
      }
    },

    canvas_mousemove(e) {
      if(this.newNodes.length == 0)
        return;

      // calc lower bounds
      let xLowerBound = this.$refs.canvas.parentElement.parentElement.scrollLeft;
      let yLowerBound = this.$refs.canvas.parentElement.parentElement.scrollTop;

      // calc mouse x and y w/ minor adjustments to keep mouse on origin node position
      let mouseX = e.clientX + xLowerBound - 60; 
      let mouseY = e.clientY + yLowerBound - 160;

      // find leftmost and topmost nodes
      let minXNode = this.newNodes.find((node) => node.minX == true);
      let minYNode = this.newNodes.find((node) => node.minY == true);

      // deltas are based off the leftmost node, the origin node (minXNode)
      let deltaX = mouseX - minXNode.x;
      let deltaY = mouseY - minXNode.y;

      // calculate the leftmost nodes' new x and topmost nodes' new y
      let newMinXCoord = minXNode.x + deltaX;
      let newMinYCoord = minYNode.y + deltaY;
      
      // don't allow nodes to be placed off screen
      if(newMinXCoord < xLowerBound || newMinYCoord < yLowerBound)
        return;
      
      for(let i = 0; i < this.newNodes.length; i++) {
        let node = this.newNodes[i];
        node.x = node.x + deltaX;
        node.y = node.y + deltaY;
        let component =  this.$refs[node._id];
        if(component && component.length > 0) {
          let element = component[0].$el;
          element.style.left = `${node.x}px`;
          element.style.top = `${node.y}px`;
        }
      }
    },

    node_click(node, e) {

      if(this.newNodes.length > 0)
        return;

      e.stopPropagation();
      if(!e.shiftKey)
        return;

      var selected = this.selectedNodes.find((selectedNode) => selectedNode.object._id == node.object._id);
      if(selected) {
        node.isSelected = false;
        this.selectedNodes = this.selectedNodes.filter((selectedNode) => selectedNode.object._id != node.object._id);
      }
      else {
        node.isSelected = true;
        this.selectedNodes.push(node);
      }
    },

    dragSelect_selectionChange(ids) {
      this.isDragging = true;
      let nodes = this.nodes.filter((node) => ids.indexOf(node.object._id) > -1);

      if(this.isShifted)
        this.selectedNodes = this.selectedNodes.concat(nodes);
      else
        this.selectedNodes = nodes;

      // reset node selection
      for(let i = 0; i < this.nodes.length; i++) {
        let node = this.nodes[i];
        node.isSelected = false;
        if(this.selectedNodes.indexOf(node) > -1) {
          node.isSelected = true;
        }
      }

      JSPlumbUtil.setDragSelection(ids);
     },

    async addButtonItem_click(type) {
      this.isLoading = true;

      var shortname = this.getNextShortname(type);
      var node = {
        object: {
          workflow: this.workflow,
          shortname: shortname
        },
        type: type
      };
      var result = await this.$store.dispatch("nodeStore/insertNode", {
        node: node
      });
      this.isLoading = false;
      if(!result.data.success) {
        EventBus.$emit("showAlert", {
          title: "Failed to Insert Node",
          message: result.data.error,
          type: "error"   
        });
      }
      else if(result.data.locked) {
        this.workflowLocked(result.data.user);
      }

      result.data.node.minX = true;
      result.data.node.minY = true;
      this.newNodes = [result.data.node];
      this.reloadNodes();
    },

    async startButton_click() {
      if (this.webSocket && this.webSocket.readyState !== WebSocket.OPEN){
        setTimeout(this.create_websocket(), 500)
      }
      var result = await Server.post("startWorkflow", {
        workflow: this.workflow._id,
        type: "app"
      }).promise;
      if (result.data.success) {
        this.runningId = result.data.runningId;
        if(result.data.isRunning) {
          this.isRunning = true;
          this.isQueued = false;
          this.$refs.logDrawer.open();
        }
        else {
          this.isQueued = true;
          this.isRunning = false;
          EventBus.$emit("showAlert", {
          title: "Added Workflow to Queue",
          message: "Nash is currently busy with another workflow. This workflow has been added to the queue and will process when Nash is available",
          type: "info"   
        });
        }
      } 
      else {
        EventBus.$emit("showAlert", {
          title: "Engine Down",
          message: result.data.error,
          type: "error"   
        });
      }
      
    },

    async stopButton_click() {
      var result = await Server.post("stopWorkflow", { runningId: this.runningId}).promise;
      if(!result.data.success) {
        EventBus.$emit("showAlert", {
          title: "Engine Down",
          message: result.data.error,
          type: "error"
        });
      }
    },

    errorsButton_click() {
      this.modalValidation = JSON.parse(JSON.stringify(this.validation));
      this.modalValidationTitle = this.workflow.name;
      this.$refs.errorsModal.show();
    },

    metadataButton_click() {
       if (this.$refs.metadataDrawer.isOpen) {
        this.$refs.metadataDrawer.close();
      } else {
        this.$refs.logDrawer.close();
        this.$refs.metadataDrawer.open();
      }
    },

    async saveWfButton_click() {

      if(!this.$refs.workflowMetadata.validate())
        return;

      this.isLoading = true;
      var result = await this.$store.dispatch("wfStore/updateWorkflow", {
        workflow: this.workflow
      });
      this.isLoading = false;

      if(!result.data.success) {
        this.$refs.workflowMetadata.modalMessage = result.data.error;
        this.$refs.workflowMetadata.modalMessageClass = "agModal-errorMessage";
      }
      else {
        this.$refs.metadataDrawer.close();
      }
      return result;
    },

    // logs drawer

    logButton_click() {
      if (this.$refs.logDrawer.isOpen) {
        this.$refs.logDrawer.close();
      } else {
        this.$refs.metadataDrawer.close();
        this.$refs.logDrawer.open();
      }
    },

    clearLogsButton_click() {
      this.logs = [];
    },

    // copying, pasting, and deleting nodes

    copyNodesButton_click() {
      let nodes = this.selectedNodes.filter((n) => n.type != "startNode"); // remove startNode from list
      this.$store.dispatch("nodeStore/copyNodes", {nodes: nodes});
    },

    async pasteNodesButton_click() {
      try {
        let result = await this.$store.dispatch("nodeStore/pasteNodes", { workflow: this.workflow});
        if(!result.data.success) {
          EventBus.$emit("showAlert", {
                title: "Error",
                message: `Unable to paste nodes to workflow. Please recopy and paste.`,
                type: "error"
              });
          return;
        }
        this.newNodes = result.data.nodes;
        this.reloadNodes();
      }
      catch(e) {
        console.log(`Failed pasteNodes call. ${e}`);
      }
    },

    deleteNodesButton_click() {
      this.$refs.deleteNodesModal.show();
    },

    async confirmDeleteNodesButton_click() {
       try {
        this.$refs.deleteNodesModal.hide();
        
        this.isLoading = true;
        let nodes = this.selectedNodes.filter((n) => n.type != "startNode"); // remove startNode from list
        let result = await this.$store.dispatch("nodeStore/deleteNodes", { workflow: this.workflow, nodes: nodes});
        this.isLoading = false;
        if(!result.data.success) {
          EventBus.$emit("showAlert", {
            title: "Error",
            message: `Unable to delete nodes. Please reselect and try again`,
            type: "error"
          });
          return;
        }
        this.isLoading = false;
        this.reloadNodes();
      }
      catch(e) {
        console.log(`Failed deleteNodes call. ${e}`);
        this.isLoading = false;
      }
    },

    // emitted events

    updatedNode(updated) {
      var plural = this.$store.getters["nodeStore/getPlural"](updated.type);
      // have to manually update workflow to handle nested fields (e.g. cases/mods in switches)
      Util.replaceInArray(this.workflow[plural], "_id", updated.object);
      this.reloadNodes();
    },

    deletedNode(deleted) {
      this.reloadNodes();
    },

    async editNode(node) {
      let lockCheck = await this.checkWorkflowLock();
      if(lockCheck)
        return;

      this.modalNode = JSON.parse(JSON.stringify(node));
      var ref = node.type + "Modal"; // e.g. inputModal

      // needs time to bind modalNode to modal's node property before showing
      setTimeout(() => {
        this.$refs[ref].show();
      }, 250)
    },

    showErrors(node) {
      this.modalValidation = JSON.parse(JSON.stringify(node));
      var name = node.object.name;
      if (!name) name = node.object.shortname;
      this.modalValidationTitle = name;
      this.$refs.errorsModal.show();
    },

    // other

    async checkWorkflowLock() {
      try {
        if(this.isReadonly)
          return false;

        let lockCheck = await Server.post("/isWorkflowLocked", {workflow: this.workflow._id}).promise;
        if(!lockCheck.data.success)
          throw lockCheck.data.error;
        else if(lockCheck.data.locked) {
          this.isReadonly = true;
          setTimeout(() => {
            EventBus.$emit("showAlert", {
              title: "Workflow Locked",
              message: `This workflow is currently being edited by ${lockCheck.data.user} and is only accessible in read-only mode.`,
              type: "error"
            });
          }, 250);
          return true;
        }

        return false;
      }
      catch(e) {
        console.log(`Failed 'isWorkflowLocked' server call. ${e.toString()}`);
          EventBus.$emit("showAlert", {
            title: "Error",
            message: e,
            type: "error"
          });
          return true;
      }
    },

    setNodes() {
      var nodes = [{
        type: "startNode",
        object: this.workflow.startNode
      }];

      for (var i = 0; i < this.nodeTypes.length; i++) {
        var type = this.nodeTypes[i];
        for (var j = 0; j < this.workflow[type.plural].length; j++) {
          let object = this.workflow[type.plural][j];

          // readjust any corrupt node positions
          if(object.x < 0)
            object.x = 0;
          if(object.y < 0) 
            object.y = 0;

          var node = {
            type: type.type,
            object: object
          };
          nodes.push(node);
        }
      }
      this.nodes = nodes;
    },

    async validateWorkflow() {
      this.validation = {
        isValid: false,
        errors: [],
        warnings: []
      };
      try {
        var result = await Server.post("validateWorkflow", {
          workflow: this.workflow._id
        }).promise;

        if (!result.data.success)
          this.validation.errors.push(result.data.error);
        else 
          this.validation = result.data;

        // add validation to nodes
        if (this.validation.nodes) {
          for (var i = 0; i < this.validation.nodes.length; i++) {
            var nodeValidation = this.validation.nodes[i];
            var node = this.nodes.find(
              a => a.object.shortname == nodeValidation.shortname
            );
            if (node) {
              node.errors = nodeValidation.errors;
              node.warnings = nodeValidation.warnings;
            }
          }
        }
      } catch (e) {
        console.log("Failed 'validateWorkflow' server call. " + e.toString());
        this.validation.errors.push("Server call to validate workflow failed. Please refresh the application");
      }
    },

    async reloadNodes() {
      this.setNodes();
      this.validateWorkflow();

      // trigger rerendering of all nodes
      this.reload++;

      // wrap in timeout so DOM has time to render nodes
      setTimeout(() => {
        JSPlumbUtil.reset();
        this.initJSPlumb();
      }, 250);
    },

    getNextShortname(type) {
      var max = 0;
      var plural = this.$store.getters["nodeStore/getPlural"](type);
      var prefix = this.$store.getters["nodeStore/getPrefix"](type);
      for (var i = 0; i < this.workflow[plural].length; i++) {
        var node = this.workflow[plural][i];
        var num = parseInt(node.shortname.replace(prefix, ""));
        if (num > max) {
          max = num;
        }
      }
      return prefix + (max + 1);
    },
    create_websocket(){
      // setup websocket
      if (this.webSocket){
        console.log("WebSocket Stat: ", this.webSocket.readyState),
        this.webSocket.close();
        this.webSocket = null;
        // this.$nextTick(() => {
        //   if ([WebSocket.CLOSING, WebSocket.OPEN].includes(this.webSocket.readyState)){
        //     this.webSocket.terminate();
        //   }
        // })
      }
      this.webSocket = Server.initWebSocket();
      this.webSocket.onopen = () => {
        let message = {
          model: "wfLog",
          workflow: this.workflow._id
        };
        message = JSON.stringify(message);
        this.webSocket.send(message);
      };
      this.webSocket.onmessage = message => {
        this.webSocket_onmessage(message);
      };
    },
    webSocket_onmessage(message) {
      var log = JSON.parse(message.data);

      // sort list of logs by time
      log.time = new Date(log.time);
      this.logs.push(log);

      Util.sortByKey(this.logs, "time");

      // check for system logs
      if (log.level == -1) {
        if (log.log.toLowerCase().indexOf("starting") > -1) {
          this.isRunning = true;
          this.$refs.logDrawer.open();
        } 
        else if (log.log.toLowerCase().indexOf("stopped") > -1) {
          this.isRunning = false;
        } 
        else if (log.log.toLowerCase().indexOf("finished") > -1) {
          this.isRunning = false;
        } 
        else if (log.log.toLowerCase().indexOf("invalid") > -1) {
          this.isRunning = false;
        }
      }

      setTimeout(() => {
        var logDrawer = this.$refs.logDrawer;
        logDrawer.scrollToBottom();
      }, 250);
    },

    getFormattedLogDate(value) {
      var date = Moment(value).format("M/DD/YY h:mm:ss a");
      return date;
    },

     hasError(field) {
      if (field == this.errorField) {
        return false; // false = error
      }
      return null;
    },
  }
};
</script>






<style scoped lang="css">
#agWfEditor-card {
  position: relative;
  height: calc(100vh - 145px);
  top: 60px;
  overflow: auto;
}

#agWfEditor-card .card-body {
  padding: 0px;
}

#agWfEditor-canvas {
  position: relative;
  width: 10000px;
  height: 10000px;
  pointer-events: all;
}

#agWfEditor-dragSelect {
  position: relative;
  width: calc(100% - 15px);
  height: calc(100% - 15px);
  left: 15px;
  top: 15px;
}

#agWfEditor-errors {
  position: fixed;
  right: 12.5px;
  top: 125px;
  font-size: 24px;
  cursor: pointer;
}

#agWfEditor-errors:hover {
  opacity: 0.5;
}

#agWfEditor-errorsIcon {
  color: #DC3545;
}

#agWfEditor-warningsIcon {
  color: #ffd40f;
}

#agWfEditor-loading {
  position: relative;
  width: 30px;
  height: 30px;
  top: 10px;
  border-left-color: #4A797C;
  border-top-color: #4A797C;
  border-bottom-color: #4A797C;
}

#agWfEditor-toolbarCenter {
  position: absolute;
  width: 300px;
  left: 50%;
  margin-left: -150px;
  text-align: center;
}

#agWfEditor-playButton, #agWfEditor-stopButton {
  font-size: 36px !important;
}

#agWfEditor-inProgress {
  position: relative;
  top: 10px;
  left: -10px;
}

#agWfEditor-inProgress .spinner-border {
  border-left-color: #DC3545;
  border-top-color: #DC3545;
  border-bottom-color: #DC3545;
}

#agWfEditor-inProgress label {
  position: relative !important;
  left: 10px !important;
  top: -7.5px !important;
  color: #DC3545;
  font-weight: bold;
}

#agWfEditor-readonlyLabel {
  position: relative !important;
  top: 15px !important;
  color: #DC3545;
  font-weight: bold;
}

#agWfEditor-noLogs {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -89px;
  margin-top: -41px;
  font-size: 24px;
  color: lightgray;
}

#agWfEditor-noLogs svg {
  font-size: 40px;
}

#agWfEditor-logTable {
  background-color: #ffffff;
  width: 100%;
  height: 100%;
  text-align: left;
  table-layout: fixed;
}

.agWfEditor-logTable-col1 {
  width: 200px !important;
  text-align: left;
}

.agWfEditor-logTable-col2 {
  width: 125px !important;
  text-align: left;
}

.agWfEditor-logMessage {
  white-space: pre-wrap;
  text-align: left !important;
}

.agWfEditor-logLevel {
  font-size: 20px;
  margin-right: 10px;
}

.agWfEditor-logLevelSystem {
  color: #4A797C;
}

.agWfEditor-logLevelError {
  color: #C82333;
  font-size: 18px;
}

.agWfEditor-logLevelWarn {
  color: #ffd40f;
}

.agWfEditor-logLevelInfo {
  color: #007bff;
}

.agWfEditor-logLevelDebug {
  color: #32cd32;
}
</style>


// WEBPACK FOOTER //
// src/components/workflow/WorkflowEditor.vue